import { DateTime } from "luxon";
import * as React from "react";
import { connect } from "react-redux";
import { Alert, Button, Modal, Group } from "@mantine/core";
import { bindActionCreators } from "redux";
import { getAppointmentsByPerson } from "../../data/actions/appointmentActions";
import { IState } from "../../data/reducers/state";
import {
  IAppointment,
  IAppointmentDetail,
} from "../../types/appointments/Appointment";
import { AppointmentStatus } from "../../types/appointments/AppointmentStatus";
import { IVenue } from "../../types/venue/Venue";
import { toDateTime } from "../../util/date+helper";
import AppointmentView from "../appointments/AppointmentView";
import { CenteredLoader } from "../common/loader";
import { SSTable } from "../common/TableData";
import { IProfileTabProps } from "./ProfileTabType";

export interface IAppointmentsHistoryProps extends IProfileTabProps {
  appointments: IAppointment<IAppointmentDetail>[];
  venues: { [key: string]: IVenue };
  actions: {
    getAppointmentsByPerson: (
      personId: string,
    ) => Promise<IAppointment<IAppointmentDetail>[]>;
  };
}

function AppointmentsHistory(props: IAppointmentsHistoryProps) {
  const [error, setError] = React.useState<string | null>(null);
  const [isLoaded, setIsLoaded] = React.useState<boolean>(false);
  const [showModal, setShowModal] = React.useState<string | null>(null);

  const venues = props.venues || {};

  const toggleModal = (appointmentId: string | null) => () =>
    setShowModal(appointmentId);

  React.useEffect(() => {
    document.title = `Stadium Supervisor - ${props.person.preferredName || props.person.firstName
      } appointments`;
  }, []);

  if (error) return <Alert color="red">{error}</Alert>;

  if (!props.isActive) return <div />;

  if (!isLoaded) {
    setIsLoaded(true);
    props.actions
      .getAppointmentsByPerson(props.person._id)
      .catch((er) => setError(er));
    return <CenteredLoader />;
  }
  if (!props.appointments) {
    return <CenteredLoader />;
  }

  return (
    <div style={{}}>
      <SSTable>
        <thead>
          <tr>
            <th>Status</th>
            <th>Type</th>
            <th>Venue</th>
            <th>Date</th>
            <th>Start</th>
            <th>Finish</th>
            <th>Total</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {props.appointments.map((a) => {
            const date = toDateTime(a.start).toLocaleString(
              DateTime.DATE_SHORT,
            );
            const start = toDateTime(a.start).toLocaleString(
              DateTime.TIME_SIMPLE,
            );
            const finish = toDateTime(a.finish).toLocaleString(
              DateTime.TIME_SIMPLE,
            );

            const greyOrBlack = a.stillAppointed
              ? {}
              : { color: "var(--color-text-disabled)" };

            return (
              <tr key={a._id}>
                <td style={greyOrBlack}>
                  <span
                    style={{
                      backgroundColor: AppointmentStatus.toColor(a.status),
                      marginRight: "10px",
                      height: "15px",
                      width: "15px",
                      borderRadius: "50%",
                      display: "inline-block",
                    }}
                  />
                  {AppointmentStatus.toString(a.status)}
                </td>
                <td style={greyOrBlack}>{a.type}</td>
                <td style={greyOrBlack}>{venues[a.details.venue]?.name}</td>
                <td style={greyOrBlack}>{date}</td>
                <td style={greyOrBlack}>{start}</td>
                <td style={greyOrBlack}>{finish}</td>
                <td style={greyOrBlack}>
                  ${Number(a.payment?.total).toFixed(2)}
                </td>
                <td style={greyOrBlack}>
                  <button
                    style={{ border: "none" }}
                    onClick={toggleModal(a._id)}
                  >
                    View
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </SSTable>
      <Modal
        opened={!!showModal}
        onClose={toggleModal(null)}
        title="Appointment"
        size="lg"
      >
        <AppointmentView appointmentId={showModal} disableEdits={true} />
        <Group justify="flex-end" mt="md">
          <Button
            color="gray"
            onClick={toggleModal(null)}
          >
            Close
          </Button>
        </Group>
      </Modal>
    </div>
  );
}

function mapStateToProps(state: IState, props: IAppointmentsHistoryProps) {
  return {
    appointments: state.appointments.byPerson?.[props.person._id]?.map(
      (ap) => state.appointments.map[ap],
    ),
    venues: state.venues.map,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ getAppointmentsByPerson }, dispatch),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(AppointmentsHistory);
